import { useState, useEffect, useCallback } from 'react';

/**
 * Include/exclude logic for appointment types per team group, used by
 * Settings/AppointmentTypeGroupSettings and TeamGroupAppointmentTypesOverlay.
 * Every appointment type is included for every group by default; a group
 * only loses one via an explicit exclusion. The API stops clinicians being
 * made available (rota mode or templates) for types excluded for all of
 * their teams' groups. A successful toggle shows no message - the button's
 * new state confirms it - so only errors are passed to setNotice.
 * @param {object} api
 * @param {function} setNotice ({ text, isError } | null) => void
 */
const useAppointmentTypeToggle = (api, setNotice) => {

    const [appointmentTypes, setAppointmentTypes] = useState([]);
    const [savingAppointmentTypeID, setSavingAppointmentTypeID] = useState(null);

    const loadAppointmentTypes = useCallback(() => {
        api.transact('TeamGroupAppointmentTypeMatrix', {})
            .then(r => r.apiResult)
            .then(r => setAppointmentTypes(r.AppointmentTypes || []))
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to load appointment types', isError: true }));
    }, [api, setNotice]);

    useEffect(() => { loadAppointmentTypes(); }, [loadAppointmentTypes]);

    const isIncluded = (appointmentType, groupID) => !appointmentType.ExcludedGroupIDs?.includes(groupID);

    const toggleAppointmentType = (appointmentType, groupID) => {

        const included = isIncluded(appointmentType, groupID);
        const params = { GroupID: groupID, AppointmentTypeID: appointmentType.AppointmentTypeID };

        const request = included
            ? api.transact('TeamGroupAppointmentTypeExclusionUpdate', params)
            : api.transact('TeamGroupAppointmentTypeExclusionDelete', params);

        setNotice(null);
        setSavingAppointmentTypeID(appointmentType.AppointmentTypeID);

        request
            .then(r => setAppointmentTypes(r.apiResult.AppointmentTypes || []))
            .catch(e => setNotice({ text: e.apiText || e.message || 'Failed to update appointment type', isError: true }))
            .finally(() => setSavingAppointmentTypeID(null));
    };

    return { appointmentTypes, savingAppointmentTypeID, isIncluded, toggleAppointmentType };
};

export default useAppointmentTypeToggle;
